import * as React from "react";
import { Monitor, RotateCw, Smartphone, Tablet, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const VIEWPORTS = {
    mobile: { width: 390, height: 844, label: "iPhone", icon: Smartphone },
    tablet: { width: 768, height: 1024, label: "Tablet", icon: Tablet },
    desktop: { width: 1280, height: 800, label: "Desktop", icon: Monitor },
} as const;

type ViewportKey = keyof typeof VIEWPORTS;

interface Props {
    /** Full URL the iframe loads — same-origin or worker preview. */
    previewUrl: string;
}

export function PreviewFrame({ previewUrl }: Props): React.ReactNode {
    const [viewport, setViewport] = React.useState<ViewportKey>("desktop");
    const [reloadKey, setReloadKey] = React.useState(0);
    const cfg = VIEWPORTS[viewport];

    return (
        <div className="flex h-full flex-col bg-muted/30">
            <div className="flex items-center justify-between border-b border-border bg-background/80 px-3 py-2">
                <div className="flex items-center gap-1">
                    {(Object.entries(VIEWPORTS) as [ViewportKey, typeof VIEWPORTS[ViewportKey]][]).map(
                        ([key, v]) => {
                            const Icon = v.icon;
                            return (
                                <Button
                                    key={key}
                                    type="button"
                                    variant={viewport === key ? "secondary" : "ghost"}
                                    size="sm"
                                    onClick={() => setViewport(key)}
                                    title={`${v.label} (${v.width}px)`}
                                    data-testid={`viewport-${key}`}
                                >
                                    <Icon className="h-4 w-4" />
                                </Button>
                            );
                        },
                    )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                        {cfg.width}×{cfg.height}
                    </span>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setReloadKey((x) => x + 1)}
                        title="Reload preview"
                        data-testid="preview-reload"
                    >
                        <RotateCw className="h-3.5 w-3.5" />
                    </Button>
                    <a
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 hover:text-foreground"
                        title="Open in new tab"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                </div>
            </div>
            <div className="flex flex-1 items-center justify-center overflow-auto p-4">
                <div
                    className="overflow-hidden rounded-md border border-border bg-background shadow-lg"
                    style={{
                        width: `${cfg.width}px`,
                        height: `${cfg.height}px`,
                        maxWidth: "100%",
                        maxHeight: "100%",
                    }}
                >
                    <iframe
                        key={`${previewUrl}#${reloadKey}`}
                        title="Draft preview"
                        src={previewUrl}
                        data-testid="preview-iframe"
                        className="h-full w-full border-0"
                        sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
                    />
                </div>
            </div>
        </div>
    );
}
